import * as Ariakit from "@ariakit/react";
import { CaretDown, SealCheck } from "@phosphor-icons/react";
import { IconButton } from "@replicate/ui";
import type { BillingConfig, Model, Version } from "../types";
import { BillingMetric } from "../types";
import ModelBilling from "./model-billing";

interface Dependency {
  model: Model;
  version?: Version;
  billing_config: BillingConfig;
}

function formatPricingDisplay(dependency: Dependency): string {
  const tiers = dependency.billing_config.current_tiers ?? [];
  if (tiers.length === 0) {
    return "-";
  }

  if (hasComplexPricing(dependency)) {
    return "Priced by multiple properties";
  }

  if (tiers.length === 1 && tiers[0].prices.length === 0) {
    return "-";
  }

  return tiers[0].prices
    .map((price) => {
      if (price.metric === BillingMetric.UnspecifiedBillingMetric) {
        return `Priced ${price.title}`;
      }

      return `${price.price} ${price.title}`;
    })
    .join(", ");
}

function hasComplexPricing(dependency: Dependency) {
  if (dependency.model._extras.is_pipeline) {
    return true;
  }

  const tiers = dependency.billing_config.current_tiers ?? [];
  if (tiers.length === 0) {
    return false;
  }

  if (tiers.length === 1 && tiers[0].prices.length <= 3) {
    return false;
  }

  return true;
}

export default function DependencyPricingTable({
  dependencies,
}: { dependencies: Dependency[] }) {
  if (!dependencies.length) {
    return null;
  }

  return (
    <div className="divide-y divide-r8-gray-6 md:border md:border-r8-gray-6">
      {dependencies.map((dependency) => {
        const { model, version, billing_config: billingConfig } = dependency;
        const displayName = version ? version._extras.name : model._extras.name;
        const hasDisclosureContent = hasComplexPricing(dependency);

        return (
          <div key={displayName}>
            <Ariakit.DisclosureProvider>
              <div className="flex items-center justify-between px-0 py-4 md:p-4 md:pr-0 bg-r8-gray-1">
                <div className="flex-1">
                  <div className="flex flex-col gap-1.5">
                    {model._extras.is_official ? (
                      <div className="flex items-center gap-1 text-r8-gray-11">
                        <SealCheck weight="fill" />
                        <span className="text-r8-xs font-semibold tracking-wider uppercase leading-none">
                          Official
                        </span>
                      </div>
                    ) : null}
                    <div className="text-r8-md md:text-r8-lg font-heading font-semibold">
                      <a
                        href={model.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {displayName}
                      </a>
                    </div>
                    <div className="md:hidden text-r8-sm">
                      <span className="font-medium">
                        {formatPricingDisplay(dependency)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="hidden md:block text-right text-r8-sm w-1/2">
                  <span className="font-medium">
                    {formatPricingDisplay(dependency)}
                  </span>
                </div>
                <div className="w-10 h-full shrink-0 ml-2">
                  {hasDisclosureContent ? (
                    <Ariakit.Disclosure
                      render={
                        <IconButton
                          className="group"
                          size="sm"
                          variant="clear"
                        />
                      }
                    >
                      <CaretDown className="group-aria-expanded:rotate-180" />
                    </Ariakit.Disclosure>
                  ) : null}
                </div>
              </div>
              {hasDisclosureContent ? (
                <Ariakit.DisclosureContent className="bg-r8-gray-2 p-6">
                  {model._extras.is_pipeline ? (
                    <>
                      Check the <a href={model.url}>{model._extras.name}</a>{" "}
                      model page for pricing details.
                    </>
                  ) : (
                    <ModelBilling
                      billingConfig={billingConfig}
                      modelName={displayName}
                    />
                  )}
                </Ariakit.DisclosureContent>
              ) : null}
            </Ariakit.DisclosureProvider>
          </div>
        );
      })}
    </div>
  );
}
